import { useCallback, useMemo } from 'react';

import { Nullable } from '@rbi-ctg/frontend';
import { IItem, ISanityLocaleImages, ISanityTextNode } from '@rbi-ctg/menu';
import { ServiceMode } from 'generated/rbi-graphql';
import { ILocaleString } from 'generated/sanity-graphql';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { itemIsAvailable } from 'utils/availability';

import { getPosVendorFromStore } from './menu/use-pos-vendor';

export interface IItemAcceptingFreeOffer {
  _id: string;
  name: ILocaleString;
}

export interface IItemsAcceptingFreeOffers {
  _key: string;
  itemAcceptingFreeOffer: IItemAcceptingFreeOffer;
  maxFreeQuantity: number;
}

export interface IAddOnSectionOption {
  _key: string;
  maxAmount: number;
  option: IItem;
  freeOption: Nullable<IItem>;
  itemsAcceptingFreeOffers: Nullable<IItemsAcceptingFreeOffers[]>;
  addOnSectionFreeModalInfos: IAddOnSectionFreeModalFieldset;
}

export interface IServiceModes {
  cateringDeliveryServiceMode: boolean;
  cateringPickUpServiceMode: boolean;
  curbsideServiceMode: boolean;
  deliveryServiceMode: boolean;
  dineInServiceMode: boolean;
  driveThruServiceMode: boolean;
  pickUpServiceMode: boolean;
  tableServiceMode: boolean;
}

export interface IAddOnSectionFreeModalFieldset {
  description: Nullable<ISanityTextNode>;
  image: Nullable<ISanityLocaleImages>;
  primaryButtonText: Nullable<ISanityTextNode>;
  showFreeAddOnModal: Nullable<boolean>;
  title: Nullable<ISanityTextNode>;
}

export interface IAddOnSection {
  _id: string;
  maxAmount: number;
  name: ISanityTextNode;
  options: IAddOnSectionOption[];
  showSectionItemsOnCart: boolean;
  sectionItemsServiceModes: IServiceModes;
  enableAddonAsFreeItem: boolean;
}

const useFeatureMenuAddOn = () => {
  const { serviceMode } = useServiceModeContext();
  const { store, prices } = useStoreContext();
  const { featureMenu, featureMenuLoading } = useFeatureMenu();

  const addOnType: string = useMemo(() => {
    switch (serviceMode) {
      case ServiceMode.CATERING_PICKUP:
      case ServiceMode.CATERING_DELIVERY:
        return 'cartAddOnSectionsCatering';
      default:
        return 'cartAddOnSections';
    }
  }, [serviceMode]);

  const filterAvailableOptions = useCallback(
    (options: IAddOnSectionOption[]) => {
      const vendor = getPosVendorFromStore(store, serviceMode);
      return options.filter(option => itemIsAvailable(option.option, vendor, prices));
    },
    [prices, serviceMode, store]
  );

  const sections: IAddOnSection[] = useMemo(
    () =>
      (featureMenu?.[addOnType] ?? []).reduce((acc: IAddOnSection[], section: IAddOnSection) => {
        const availableOptions = filterAvailableOptions(section?.options ?? []);
        // Returning section only if it has available options
        return availableOptions.length ? [...acc, { ...section, options: availableOptions }] : acc;
      }, []),
    [featureMenu, addOnType, filterAvailableOptions]
  );

  return {
    sections,
    loading: featureMenuLoading,
  };
};

export default useFeatureMenuAddOn;
